<script lang="ts">
	import { devices_settings } from 'src/stores/device-runtime/status';
	//Design system components
	import { Button, Container, Input, Radio, Select, Stack, Text } from '@Shelly/shelly-design-system';
	//Helpers
	import { onSettingsResponse } from 'src/helpers/device_settings_helpers';
	import { real_id } from 'src/helpers/interface_helpers';
	//Services
	import { format } from 'svelte-i18n';
	import { gen2Request } from 'src/services/gen_2_http_reqs';
	//Stores
	import {
		getDeviceStatus,
		getDeviceSettings,
		readDeviceSettings,
	} from 'src/stores/device-runtime/status-selectors';
	//Types
	import type { SettingRequest } from 'src/types/requests/settings/setting';
	//Svelte hooks
	import CustomPercentExpressionG2 from '../CustomPercentExpressionG2.svelte';
	import { capFirstChar, getErrorOrInfoMessageForMinAndMax } from 'src/helpers/utilities';

	export let device;

	$: settingsStore = getDeviceSettings(device.id);
	let settings = readDeviceSettings(device.id);
	$: settings = $settingsStore ?? settings;
	const loading = { name: undefined, report_thr: undefined };

	const voltmeterIDs = ['voltmeter:0', 'voltmeter:1', 'voltmeter:2', 'voltmeter:3', 'voltmeter:4'];

	let selectedIndex = 0;
	let existingVoltmeterIDs = voltmeterIDs
		.filter((id) => Object.keys(settings ?? {}).includes(id))
		.map((id) => {
			const index = voltmeterIDs.indexOf(id);
			if (settings[id]?.name) {
				id = settings[id].name;
			}
			return id;
		});
	let deltaThreshold = settings[voltmeterIDs[selectedIndex]].report_thr;

	let min = 0.1;
	let max = 5;

	const updateExistingVoltmeterIDs = () => {
		existingVoltmeterIDs = voltmeterIDs
			.filter((id) => Object.keys($settingsStore ?? {}).includes(id))
			.map((id) => {
				const index = voltmeterIDs.indexOf(id);
				if (settings[id]?.name) {
					id = settings[id].name;
				}
				return id;
			});
	};

	const changeName = async () => {
		const component = voltmeterIDs[selectedIndex];
		/**{"method":"voltmeter.setconfig","id":11,"src":"e2812e02-e2b2-41e2-8cf8-c20db1309a1a","params":{"id":0,"config":{"name":"Volt 01"}}}	1732267867.112612*/

		loading.name = true;
		const params = {
			id: selectedIndex,
			config: {
				name: settings[component].name,
			},
		};

		const res: SettingRequest = await gen2Request(
			device,
			`${component.split(':')[0]}.SetConfig`,
			params,
		);
		const response = onSettingsResponse(res, device);
		if (response) {
			devices_settings.update_devices_settings(device.id, settings);
			updateExistingVoltmeterIDs();
		} else {
		}
		loading.name = false;
	};
	const changeDelta = async () => {
		const component = voltmeterIDs[selectedIndex];
		/**{"method":"voltmeter.setconfig","id":13,"src":"e2812e02-e2b2-41e2-8cf8-c20db1309a1a","params":{"id":0,"config":{"report_thr":4}}}	1732267914.2572012*/

		loading.report_thr = true;
		const params = {
			id: selectedIndex,
			config: {
				report_thr: settings[component].report_thr,
			},
		};

		const res: SettingRequest = await gen2Request(
			device,
			`${component.split(':')[0]}.SetConfig`,
			params,
		);
		const response = onSettingsResponse(res, device);
		if (response) {
			devices_settings.update_devices_settings(device.id, settings);
			updateExistingVoltmeterIDs();
		} else {
		}
		loading.report_thr = false;
	};
	$: deltaThresholdData = getErrorOrInfoMessageForMinAndMax(deltaThreshold, min, max);
</script>

<Text class="mb-s">{$format('label_selected', { default: 'Selected:' })}</Text>
<Select
	class="mb-s"
	options={existingVoltmeterIDs}
	value={existingVoltmeterIDs[selectedIndex]}
	on:change={(e) => {
		console.warn(e.detail);
		selectedIndex = existingVoltmeterIDs.indexOf(e.detail);
	}}
	id="select-3vhqu3v"
	aria-label={$format('sub_title_selected_devices', {
		default: 'Selected devices',
	})}
/>
<hr class="mb-m mt-s" style="border-color: var(--color-base);" />
{#if existingVoltmeterIDs[selectedIndex] && settings[voltmeterIDs[selectedIndex]]}
	<Text class="mb-s">{$format('label_name', { default: 'Name' })}</Text>
	<Input
		bind:value={settings[voltmeterIDs[selectedIndex]].name}
		size="m"
		class="mb-s"
		label={$format('label_name', { default: 'Name' })}
		on:enter={changeName}
		id="input-5omzzwx"
		aria-labelledby="input-5omzzwx"
	/>
	<Button size="m" width="full" class="mb-s" on:click={changeName} loading={loading.name}>
		{$format('button_save')}
	</Button>
	<hr class="mb-m mt-s" style="border-color: var(--color-base);" />
	<Text class="mb-s">{capFirstChar($format('delta_threshold', { default: 'Delta Threshold' }))}</Text>
	<Input
		bind:value={deltaThreshold}
		size="m"
		class="mb-s"
		step={0.1}
		label={$format('delta_threshold')}
		error={deltaThresholdData.error}
		info={deltaThresholdData.info}
		on:enter={changeDelta}
		id="input-34diw4e"
		aria-labelledby="input-34diw4e"
	/>
	<Button size="m" width="full" class="mb-s" on:click={changeDelta} loading={loading.report_thr}>
		{$format('button_save')}
	</Button>
	<hr class="mb-m mt-s" style="border-color: var(--color-base);" />
	<Text class="mb-s"
		>{capFirstChar(
			$format('title_input_counter_custom_expression_formula', {
				default: 'Custom expression',
			}),
		)}</Text
	>
	<CustomPercentExpressionG2
		{device}
		component={voltmeterIDs[selectedIndex]}
		channel={selectedIndex}
		type={'xvoltage'}
	/>
{/if}
